import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";

import logo from "../../../images/ensss_logo.png";

/**
 * Relevé de notes officiel de l'ENSSS (bilingue FR / AR) : une page par
 * semestre, au format du relevé papier de la direction des études.
 *
 * Les données viennent de GET /exam-results/transcript/:studentId/data
 * (champ `semesters`, construit par backend/src/services/transcript.builder.js).
 */

/* =========================================================
 * POLICES
 * ======================================================= */

Font.register({ family: "Louguiya", src: "/fonts/Louguiya.ttf", fontWeight: "normal" });
Font.register({ family: "Louguiya", src: "/fonts/Louguiya Bold.ttf", fontWeight: "bold" });
Font.register({ family: "LouguiyaFR", src: "/fonts/LouguiyaFR.ttf", fontWeight: "normal" });
Font.register({ family: "LouguiyaFR", src: "/fonts/LouguiyaFR Bold.ttf", fontWeight: "bold" });

/* =========================================================
 * HELPERS
 * ======================================================= */

const containsArabic = (value) =>
    value !== null && value !== undefined && /[؀-ۿݐ-ݿࢠ-ࣿ]/.test(String(value));

/** Nombre au format français (virgule décimale), ou chaîne vide si absent. */
const formatNumber = (value, digits = 2, fixed = false) => {
    if (value === null || value === undefined || value === "") return "";
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: fixed ? digits : 0,
        maximumFractionDigits: digits,
        useGrouping: false,
    }).format(number);
};

const formatDate = (date) => {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(parsed);
};

/** Date ISO courte (AAAA-MM-JJ), comme sur le relevé papier. */
const formatIsoDate = (date) => {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};

/**
 * Les codes de module tiennent dans leur colonne (ex. SP51). Un code long
 * (ex. TEST-UE1-L1S1) est coupé au tiret le plus proche du milieu.
 */
const LONG_CODE_LENGTH = 8;
const breakableCode = (code) => {
    const value = code ? String(code) : "";
    if (value.length <= LONG_CODE_LENGTH) return value;
    const middle = value.length / 2;
    let cut = -1;
    for (let i = value.indexOf("-"); i !== -1; i = value.indexOf("-", i + 1)) {
        if (cut === -1 || Math.abs(i - middle) < Math.abs(cut - middle)) cut = i;
    }
    return cut === -1 ? value : `${value.slice(0, cut + 1)}\n${value.slice(cut + 1)}`;
};

/** Texte qui choisit sa police selon son contenu (arabe ou latin). */
const T = ({ children, style, bold = false }) => {
    const arabic = containsArabic(children);
    return (
        <Text
            style={[
                style,
                { fontFamily: arabic ? "Louguiya" : "LouguiyaFR", fontWeight: bold ? "bold" : "normal" },
            ]}
        >
            {children}
        </Text>
    );
};

/* =========================================================
 * EN-TÊTE BILINGUE
 * ======================================================= */

const Header = () => (
    <View style={styles.header}>
        <View style={styles.headerFr}>
            <T style={styles.ministry} bold>Ministère de la Santé</T>
            <T style={styles.ministrySmall} bold>Ministère de l'Enseignement Supérieur et de la Recherche Scientifique</T>
            <T style={styles.school}>Ecole Nationale Supérieure des Sciences de la Santé</T>
            <T style={styles.direction} bold>Direction des études</T>
        </View>

        <Image src={logo} style={styles.logo} />

        <View style={styles.headerAr}>
            <T style={[styles.ministry, styles.rtl]} bold>وزارة الصحة</T>
            <T style={[styles.ministryAr, styles.rtl]} bold>وزارة التعليم العالي والبحث العلمي</T>
            <T style={[styles.school, styles.rtl]}>المدرسة الوطنية العليا لعلوم الصحة</T>
            <T style={[styles.direction, styles.rtl]}>إدارة الدروس</T>
        </View>
    </View>
);

const Title = ({ semester, academicYear }) => {
    const { ordinalFr, ordinalAr, name } = semester.semester || {};
    const titleFr = ordinalFr ? `Relevé de Notes du ${ordinalFr} Semestre` : `Relevé de Notes - ${name || "Semestre"}`;
    const titleAr = ordinalAr ? `كشف درجات السداسي ${ordinalAr}` : "كشف الدرجات";

    return (
        <View style={styles.titleBlock}>
            <T style={styles.titleAr} bold>{titleAr}</T>
            <T style={styles.titleFr} bold>{titleFr}</T>
            <View style={styles.yearRow}>
                <T style={styles.yearLabel} bold>Année Universitaire :</T>
                <T style={styles.yearValue} bold>{academicYear || "-"}</T>
                <T style={styles.yearLabel} bold>: السنة الجامعية</T>
            </View>
        </View>
    );
};

/* =========================================================
 * IDENTITÉ DE L'ÉTUDIANT
 * ======================================================= */

const StudentBlock = ({ student }) => {
    const level = student.level || {};
    const department = student.department || {};
    const programme = [
        level.name && level.name !== "-" ? level.name : null,
        level.code && level.code !== "-" ? `(${level.code})` : null,
        department.name && department.name !== "-" ? department.name : null,
    ].filter(Boolean).join(" ");

    return (
        <View style={styles.studentBlock}>
            <View style={styles.studentRow}>
                <T style={styles.studentLabel}>L'étudiant(e) :</T>
                <T style={styles.studentName} bold>{student.fullName || "-"}</T>
                <T style={[styles.studentLabelAr, styles.rtl]}>الطالب (ة) :</T>
            </View>

            <View style={styles.studentRow}>
                <T style={styles.studentLabel}>Né(e) le :</T>
                <T style={styles.studentValue} bold>{formatIsoDate(student.dateOfBirth)}</T>
                <T style={styles.studentLabelSmall}>à /en :</T>
                <T style={styles.studentPlace} bold>{student.placeOfBirth || "-"}</T>
                <T style={styles.studentLabelSmall}>في :</T>
                <T style={styles.studentValue} bold>{formatIsoDate(student.dateOfBirth)}</T>
                <T style={[styles.studentLabelAr, styles.rtl]}>المولود (ة) :</T>
            </View>

            <View style={styles.studentRow}>
                <T style={styles.studentLabel}>Inscrit(e) sous le no :</T>
                <T style={styles.studentValueWide} bold>{student.registrationNumber || "-"}</T>
                <T style={styles.studentValueWide} bold>{student.registrationNumber || "-"}</T>
                <T style={[styles.studentLabelAr, styles.rtl]}>مسجل (ة) تحت الرقم :</T>
            </View>

            <View style={styles.studentRow}>
                <T style={styles.studentLabelShort}>En :</T>
                <T style={styles.programme} bold>{programme || "-"}</T>
                <T style={[styles.studentLabelShortAr, styles.rtl]}>في :</T>
            </View>
        </View>
    );
};

/* =========================================================
 * TABLEAU DES NOTES
 * ======================================================= */

const Cell = ({ children, style, textStyle, bold = false, align = "center" }) => (
    <View style={[styles.cell, style]}>
        <T style={[styles.cellText, textStyle, { textAlign: align }]} bold={bold}>
            {children === null || children === undefined ? "" : String(children)}
        </T>
    </View>
);

const GradesTable = ({ semester }) => {
    const semesterLabel = semester.semester?.ordinalFr
        ? `${semester.semester.ordinalFr} Semestre`
        : semester.semester?.name || "Semestre";

    return (
        <View style={styles.table}>
            {/* En-tête du tableau */}
            <View style={styles.row}>
                <Cell style={[styles.colModule, styles.headCell]} bold>Module</Cell>
                <Cell style={[styles.colElement, styles.headCell]} bold>Element</Cell>
                <View style={styles.colGrades}>
                    <View style={[styles.cell, styles.headCell, styles.cellNoRight]}>
                        <T style={[styles.cellText, { textAlign: "center" }]} bold>{semesterLabel}</T>
                    </View>
                    <View style={styles.row}>
                        <Cell style={styles.colTp} bold>TP</Cell>
                        <Cell style={styles.colCc} bold>CC</Cell>
                        <Cell style={styles.colExam} bold>EXAM</Cell>
                        <Cell style={styles.colRat} bold>RAT</Cell>
                        <Cell style={styles.colNfe} bold>NFE</Cell>
                        <Cell style={[styles.colCredit, styles.cellNoRight]} bold>Credit</Cell>
                    </View>
                </View>
                <Cell style={[styles.colDecision, styles.headCell]} bold>Décision</Cell>
            </View>

            {semester.modules.map((module) => (
                <View key={module.id} wrap={false}>
                    {/* Ligne du module : code, intitulé et moyenne */}
                    <View style={styles.row}>
                        <Cell
                            style={styles.colModule}
                            textStyle={String(module.code || "").length > LONG_CODE_LENGTH && styles.cellTextSmall}
                            bold
                        >
                            {breakableCode(module.code)}
                        </Cell>
                        <Cell style={[styles.colElement, styles.moduleCell]} bold>{module.name}</Cell>
                        <Cell style={[styles.colGradesMerged, styles.moduleCell]} bold>
                            {formatNumber(module.average, 3, true)}
                        </Cell>
                        <Cell style={[styles.colDecision, styles.moduleCell]} />
                    </View>

                    {/* Lignes des éléments */}
                    {module.elements.map((element, index) => (
                        <View key={element.id} style={styles.row}>
                            {/* Colonne module fusionnée visuellement sur les lignes des éléments */}
                            <Cell
                                style={[
                                    styles.colModule,
                                    index < module.elements.length - 1 && styles.cellBlank,
                                ]}
                            />
                            <Cell style={styles.colElement} align="left">{element.name}</Cell>
                            <Cell style={styles.colTp}>{formatNumber(element.tp)}</Cell>
                            <Cell style={styles.colCc}>{formatNumber(element.cc)}</Cell>
                            <Cell style={styles.colExam}>{formatNumber(element.exam)}</Cell>
                            <Cell style={styles.colRat}>{formatNumber(element.rat)}</Cell>
                            <Cell style={styles.colNfe}>{formatNumber(element.nfe)}</Cell>
                            <Cell style={styles.colCredit}>{element.credits || ""}</Cell>
                            <Cell style={styles.colDecision}>{element.decision || ""}</Cell>
                        </View>
                    ))}
                </View>
            ))}

            {/* Totaux du semestre */}
            <View style={styles.row} wrap={false}>
                <Cell style={styles.colLabelMerged} bold>Moyenne du semestre | 20</Cell>
                <Cell style={styles.colGradesMerged} bold>{formatNumber(semester.average, 3, true)}</Cell>
                <Cell style={styles.colDecision} bold>
                    {semester.isComplete ? (semester.validated ? "Validé" : "Non validé") : ""}
                </Cell>
            </View>
            <View style={styles.row} wrap={false}>
                <Cell style={styles.colLabelMerged} bold>{`Credits | ${semester.creditsTotal}`}</Cell>
                <Cell style={styles.colGradesMerged} bold>{formatNumber(semester.creditsEarned, 2, true)}</Cell>
                <Cell style={styles.colDecision} />
            </View>
        </View>
    );
};

/* =========================================================
 * LÉGENDE, DATE ET SIGNATURES
 * ======================================================= */

const FooterBlock = () => (
    <View wrap={false}>
        <View style={styles.legendRow}>
            <View style={styles.legendLeft}>
                <T style={styles.legend}>C=Capitalisé</T>
                <T style={styles.legend}>CC=Contrôle Continu</T>
                <T style={styles.legend}>EXAM=Examen</T>
                <T style={styles.legend}>NV=Non Validé</T>
            </View>

            <View style={styles.legendCenter}>
                <T style={styles.placeDate} bold>{`Nouakchott, le   ${formatDate(new Date())}`}</T>
                <T style={[styles.placeDate, styles.rtl]}>انواكشوط بتاريخ</T>
            </View>

            <View style={styles.legendRight}>
                <T style={styles.legend}>NC=Non Capitalisé</T>
                <T style={styles.legend}>NFE=Note Finale de l'Elément</T>
                <T style={styles.legend}>R=Rattrapage</T>
                <T style={styles.legend}>V=Validé</T>
            </View>
        </View>

        <View style={styles.signatures}>
            <View style={styles.signatureLeft}>
                <T style={[styles.signatureAr, styles.rtl]} bold>رئيس قسم القطب المشترك</T>
                <T style={styles.signatureFr} bold>Chef département de pôle transversal</T>
            </View>
            <View style={styles.signatureRight}>
                <T style={[styles.signatureAr, styles.rtl]} bold>رئيس قسم علوم و تقنيات الصحة</T>
                <T style={styles.signatureFr} bold>Chef département des sciences et techniques de la santé</T>
            </View>
        </View>

        <View style={styles.signatureCenter}>
            <T style={[styles.signatureAr, styles.rtl]} bold>مدير الدروس</T>
            <T style={styles.signatureFr} bold>Directeur des études</T>
        </View>
    </View>
);

const PageNote = () => (
    <View style={styles.pageNote} fixed>
        <T style={styles.pageNoteText}>NB: Il ne peut être délivré qu'un seul exemplaire de ce relevé</T>
        <T style={[styles.pageNoteText, styles.rtl]}>تنبيه : لاتسلم إلا نسخة واحدة من هذا الكشف</T>
    </View>
);

/* =========================================================
 * STYLES
 * ======================================================= */

const BORDER = "#000000";
const MODULE_BG = "#bfbfbf";

// Largeurs des colonnes (A4 = 595pt, marges de 30pt => 535pt utiles).
const W = {
    module: 50,
    element: 195,
    tp: 30,
    cc: 38,
    exam: 38,
    rat: 34,
    nfe: 40,
    credit: 36,
    decision: 74,
};
const GRADES_WIDTH = W.tp + W.cc + W.exam + W.rat + W.nfe + W.credit;

const styles = StyleSheet.create({
    page: {
        paddingTop: 28,
        paddingBottom: 50,
        paddingHorizontal: 30,
        fontFamily: "LouguiyaFR",
        fontSize: 9,
        color: "#000000",
        backgroundColor: "#ffffff",
    },
    rtl: {
        textAlign: "right",
    },

    /* En-tête */
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },
    headerFr: {
        width: 230,
    },
    headerAr: {
        width: 230,
        alignItems: "flex-end",
    },
    logo: {
        width: 62,
        height: 62,
        marginTop: 2,
    },
    ministry: {
        fontSize: 12,
    },
    ministrySmall: {
        fontSize: 7,
        marginTop: 1,
    },
    ministryAr: {
        fontSize: 10,
        marginTop: 1,
    },
    school: {
        fontSize: 9.5,
        marginTop: 4,
    },
    direction: {
        fontSize: 9,
        marginTop: 2,
    },

    /* Titre */
    titleBlock: {
        alignItems: "center",
        marginTop: 8,
    },
    titleAr: {
        fontSize: 11,
    },
    titleFr: {
        fontSize: 11,
        marginTop: 3,
    },
    yearRow: {
        flexDirection: "row",
        alignItems: "center",
        marginTop: 4,
    },
    yearLabel: {
        fontSize: 11,
    },
    yearValue: {
        fontSize: 11,
        marginHorizontal: 22,
    },

    /* Étudiant */
    studentBlock: {
        marginTop: 12,
        marginBottom: 8,
    },
    studentRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 5,
    },
    studentLabel: {
        width: 105,
        fontSize: 9.5,
    },
    studentLabelShort: {
        width: 40,
        fontSize: 9.5,
    },
    studentLabelSmall: {
        width: 34,
        fontSize: 9.5,
        textAlign: "center",
    },
    studentLabelAr: {
        flex: 1,
        fontSize: 9.5,
    },
    studentLabelShortAr: {
        width: 40,
        fontSize: 9.5,
    },
    studentName: {
        width: 330,
        fontSize: 10.5,
        textAlign: "center",
    },
    studentValue: {
        width: 70,
        fontSize: 10,
        textAlign: "center",
    },
    studentValueWide: {
        width: 165,
        fontSize: 10,
    },
    studentPlace: {
        width: 120,
        fontSize: 10,
        textAlign: "center",
    },
    programme: {
        flex: 1,
        fontSize: 11,
        textAlign: "center",
    },

    /* Tableau */
    table: {
        borderTopWidth: 1.2,
        borderLeftWidth: 1.2,
        borderColor: BORDER,
    },
    row: {
        flexDirection: "row",
    },
    cell: {
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER,
        paddingVertical: 2.5,
        paddingHorizontal: 3,
        justifyContent: "center",
    },
    cellNoRight: {
        borderRightWidth: 0,
    },
    cellBlank: {
        borderBottomWidth: 0,
    },
    cellText: {
        fontSize: 8.5,
    },
    cellTextSmall: {
        fontSize: 7,
    },
    headCell: {
        paddingVertical: 4,
    },
    moduleCell: {
        backgroundColor: MODULE_BG,
    },
    colModule: { width: W.module },
    colElement: { width: W.element },
    colGrades: { width: GRADES_WIDTH, borderRightWidth: 1, borderColor: BORDER },
    colTp: { width: W.tp },
    colCc: { width: W.cc },
    colExam: { width: W.exam },
    colRat: { width: W.rat },
    colNfe: { width: W.nfe },
    colCredit: { width: W.credit },
    colDecision: { width: W.decision },
    colGradesMerged: { width: GRADES_WIDTH },
    colLabelMerged: { width: W.module + W.element },

    /* Légende et signatures */
    legendRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 8,
    },
    legendLeft: {
        width: 150,
    },
    legendCenter: {
        width: 200,
        flexDirection: "row",
        justifyContent: "space-between",
    },
    legendRight: {
        width: 140,
    },
    legend: {
        fontSize: 9,
        marginBottom: 1,
    },
    placeDate: {
        fontSize: 9.5,
    },
    signatures: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 10,
    },
    signatureLeft: {
        width: 240,
        alignItems: "flex-start",
    },
    signatureRight: {
        width: 260,
        alignItems: "flex-end",
    },
    signatureCenter: {
        alignItems: "center",
        marginTop: 12,
    },
    signatureAr: {
        fontSize: 10,
    },
    signatureFr: {
        fontSize: 9.5,
        marginTop: 2,
    },

    /* Note de bas de page */
    pageNote: {
        position: "absolute",
        bottom: 22,
        left: 30,
        right: 30,
        flexDirection: "row",
        justifyContent: "space-between",
    },
    pageNoteText: {
        fontSize: 8.5,
    },

    empty: {
        marginTop: 20,
        textAlign: "center",
        fontSize: 10,
    },
});

/* Bandeau affiché sur chaque page quand le relevé contient des résultats non publiés. */
const ProvisionalBanner = () => (
    <Text
        fixed
        style={{
            position: "absolute", top: 10, left: 0, right: 0, textAlign: "center",
            fontFamily: "LouguiyaFR", fontWeight: "bold", fontSize: 9, color: "#b91c1c",
        }}
    >
        DOCUMENT PROVISOIRE - RÉSULTATS NON PUBLIÉS - NE PAS REMETTRE À L'ÉTUDIANT
    </Text>
);

/* =========================================================
 * DOCUMENT
 * ======================================================= */

const TranscriptPDF = ({ transcript = {} }) => {
    const student = transcript.student || {};
    const semesters = Array.isArray(transcript.semesters) ? transcript.semesters : [];

    return (
        <Document title="Relevé de notes" author="ENSSS" subject="Relevé de notes" creator="ENSSS">
            {semesters.length ? (
                semesters.map((semester) => (
                    <Page key={`${semester.academicYear?.id}-${semester.semester?.id}`} size="A4" style={styles.page}>
                        {transcript.includesProvisional && <ProvisionalBanner />}
                        <Header />
                        <Title
                            semester={semester}
                            academicYear={semester.academicYear?.name || semester.academicYear?.code || transcript.academicYear}
                        />
                        <StudentBlock student={student} />
                        <GradesTable semester={semester} />
                        <FooterBlock />
                        <PageNote />
                    </Page>
                ))
            ) : (
                <Page size="A4" style={styles.page}>
                    {transcript.includesProvisional && <ProvisionalBanner />}
                    <Header />
                    <StudentBlock student={student} />
                    <T style={styles.empty}>Aucun résultat disponible.</T>
                    <PageNote />
                </Page>
            )}
        </Document>
    );
};

export default TranscriptPDF;